import React, { useState, useEffect } from 'react';
import ApiService from '../services/api';
import './ResumeManagementPopup.css';

const ResumeManagementPopup = ({ isOpen, onClose, onResumeSelect }) => {
  const [resumes, setResumes] = useState([]);
  const [defaultResume, setDefaultResume] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchResumes();
    }
  }, [isOpen]);

  const fetchResumes = async () => {
    setLoading(true);
    try {
      const response = await ApiService.get('/user/resumes');
      if (response.success) {
        setResumes(response.data || []);
        setDefaultResume(response.default_resume || null);
      }
    } catch (error) {
      console.error('Error fetching resumes:', error);
      setError('Failed to load resumes');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a PDF or Word document');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError('');

    try {
      const formData = new FormData();
      formData.append('resume', file);

      const response = await ApiService.uploadFile('/user/resumes', formData, (progressEvent) => {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(progress);
      });

      if (response.success) {
        // Refresh the list to get updated default resume status
        // This will update both resumes and defaultResume state
        const resumeResponse = await ApiService.get('/user/resumes');
        if (resumeResponse.success) {
          setResumes(resumeResponse.data || []);
          const newDefaultResumeId = resumeResponse.default_resume || null;
          setDefaultResume(newDefaultResumeId);
          
          // If there's a default resume, notify parent to refresh the dashboard
          if (newDefaultResumeId && onResumeSelect) {
            const defaultResumeObj = resumeResponse.data.find(r => r.id === newDefaultResumeId);
            if (defaultResumeObj) {
              onResumeSelect(defaultResumeObj);
            }
          }
        }
        
        setError('');
      }
    } catch (error) {
      console.error('Error uploading resume:', error);
      setError('Failed to upload resume. Please try again.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleSetDefault = async (resumeId) => {
    try {
      const response = await ApiService.put(`/user/resumes/${resumeId}/set-default`);
      if (response.success) {
        setDefaultResume(resumeId);
        
        // Find the resume object and notify parent
        const resume = resumes.find(r => r.id === resumeId);
        if (resume && onResumeSelect) {
          onResumeSelect(resume);
        }
        
        setError('');
      }
    } catch (error) {
      console.error('Error setting default resume:', error);
      setError('Failed to set default resume');
    }
  };

  const handleDeleteResume = async (resumeId) => {
    if (!window.confirm('Are you sure you want to delete this resume?')) {
      return;
    }

    try {
      const response = await ApiService.delete(`/user/resumes/${resumeId}`);
      if (response.success) {
        await fetchResumes(); // Refresh the list
        if (defaultResume === resumeId) {
          setDefaultResume(null);
        }
        setError('');
      }
    } catch (error) {
      console.error('Error deleting resume:', error);
      setError('Failed to delete resume');
    }
  };

  const handleDownloadResume = async (resume) => {
    console.log('Downloading resume:', resume);
    
    if (resume.id) {
      try {
        // Use ApiService for consistent authentication
        const response = await fetch(`${ApiService.getFullURL('/user/resumes/' + resume.id + '/download')}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${ApiService.getToken()}`,
            'Accept': 'application/octet-stream',
          },
        });

        if (response.ok) {
          const blob = await response.blob();
          const fileName = resume.original_name || resume.name || 'resume';
          
          // Create download link
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          // Clean up the object URL
          window.URL.revokeObjectURL(url);
          
          console.log('Resume downloaded successfully:', fileName);
        } else {
          console.error('Download failed with status:', response.status);
          setError('Failed to download resume');
        }
      } catch (error) {
        console.error('Error downloading resume:', error);
        setError('Failed to download resume');
      }
    } else {
      console.error('No resume ID found for download:', resume);
      setError('Unable to download resume - resume ID not found');
    }
  };

  const handleSelectResume = (resume) => {
    onResumeSelect(resume);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="resume-popup-overlay">
      <div className="resume-popup">
        <div className="resume-popup-header">
          <h2>Manage Resumes</h2>
          <button className="close-btn" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="resume-popup-content">
          {/* Upload Section */}
          <div className="upload-section">
            {resumes.length > 0 ? (
              <div className="add-more-section">
                <input
                  type="file"
                  id="resume-upload"
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
                <button 
                  className="add-more-btn"
                  onClick={() => document.getElementById('resume-upload').click()}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                  <span>Add More Resumes</span>
                </button>
              </div>
            ) : (
              <div className="upload-area">
                <input
                  type="file"
                  id="resume-upload"
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
                <label htmlFor="resume-upload" className="upload-label">
                  <div className="upload-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14,2 14,8 20,8"></polyline>
                      <line x1="12" y1="5" x2="12" y2="19"></line>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                  </div>
                  <div className="upload-text">
                    <h3>Upload Your First Resume</h3>
                    <p>Click to select PDF or Word document (max 5MB)</p>
                  </div>
                </label>
              </div>
            )}

            {/* Upload Progress */}
            {uploading && (
              <div className="upload-progress">
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <span className="progress-text">{Math.round(uploadProgress)}%</span>
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="error-message">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
              {error}
            </div>
          )}

          {/* Resumes List */}
          <div className="resumes-section">
            <h3>Your Resumes</h3>
            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <span>Loading resumes...</span>
              </div>
            ) : resumes.length > 0 ? (
              <div className="resumes-list">
                {resumes.map((resume) => (
                  <div key={resume.id} className={`resume-item ${defaultResume === resume.id ? 'default' : ''}`}>
                    <div className="resume-info">
                      <div className="resume-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                          <polyline points="14,2 14,8 20,8"></polyline>
                          <line x1="16" y1="13" x2="8" y2="13"></line>
                          <line x1="16" y1="17" x2="8" y2="17"></line>
                          <polyline points="10,9 9,9 8,9"></polyline>
                        </svg>
                      </div>
                      <div className="resume-details">
                        <div className="resume-name">
                          {resume.name || 'Resume'}
                          {defaultResume === resume.id && (
                            <span className="default-badge">Default</span>
                          )}
                        </div>
                        <div className="resume-meta">
                          <span className="resume-size">
                            {resume.size ? `${(resume.size / 1024).toFixed(1)} KB` : ''}
                          </span>
                          <span className="resume-date">
                            {resume.created_at ? new Date(resume.created_at).toLocaleDateString() : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="resume-actions">
                      <button 
                        className="action-btn download-btn"
                        onClick={() => handleDownloadResume(resume)}
                        title="Download Resume"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                          <polyline points="7,10 12,15 17,10"></polyline>
                          <line x1="12" y1="15" x2="12" y2="3"></line>
                        </svg>
                      </button>
                      {defaultResume !== resume.id && (
                        <button 
                          className="action-btn set-default-btn"
                          onClick={() => handleSetDefault(resume.id)}
                          title="Set as Default"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9 12l2 2 4-4"></path>
                            <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"></path>
                          </svg>
                        </button>
                      )}
                      <button 
                        className="action-btn delete-btn"
                        onClick={() => handleDeleteResume(resume.id)}
                        title="Delete Resume"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3,6 5,6 21,6"></polyline>
                          <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-resumes">
                <div className="no-resumes-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14,2 14,8 20,8"></polyline>
                  </svg>
                </div>
                <h4>No Resumes Yet</h4>
                <p>Upload your first resume to get started</p>
              </div>
            )}
          </div>
        </div>

        <div className="resume-popup-footer">
          <button className="cancel-btn" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResumeManagementPopup;
